import { assertNever } from 'assert-never';
import React, { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Column } from '@narvar/nth-grid';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { SemibigHeader } from '@narvar/nth-bit-header';
import Paragraph from '@narvar/nth-bit-paragraph';
import SelectList from '../../form-fields/RegisteredSelectList';
import TextArea from '../../form-fields/RegisteredTextArea';
import TextLink from '../../TextLink';
import {
  FormRow,
  FormColumn,
  ActionsRow,
  LasershipLogoWrapper,
  LasershipLogo,
} from '../../form-layout';
import StepHeader from '../../StepHeader';
import ErrorAlert from '../../ErrorAlert';
import DeliveryInstructionsSublimeOption, {
  SublimeOptionFieldValues,
} from '../../DeliveryInstructionsSublimeOption';

enum InputNames {
  leaveAt = 'leaveAt',
  note = 'note',
  sublimeOptin = 'sublimeOptin',
  sublimeEmail = 'sublimeEmail',
}

type LeaveAtValue =
  | 'Front door'
  | 'Side door'
  | 'Rear door'
  | 'Garage'
  | 'Reception'
  | 'other';

export type FormData = {
  [InputNames.leaveAt]: LeaveAtValue;
  [InputNames.note]: string;
} & SublimeOptionFieldValues;

interface DeliveryInstructionsProps {
  featureName: string;
  onLeaveAtChange: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
}

const leaveAtOptions: LeaveAtValue[] = [
  'Front door',
  'Side door',
  'Rear door',
  'Garage',
  'Reception',
  'other',
];

const FormPanel: FC<DeliveryInstructionsProps> = ({
  featureName,
  onLeaveAtChange,
  onSubmit,
  onCancel,
}) => {
  const methods = useForm<FormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      sublimeOptin: true,
    },
  });

  const {
    trigger,
    watch,
    setValue,
    handleSubmit,
    register,
    getValues,
    formState: { errors, isValid, isSubmitting },
  } = methods;

  const {
    deliveryInstructionsLeaveInstructionsHeader,
    deliveryInstructionsLeaveInstructionsSubheader,
    deliveryInstructionsSubmitInstructionsButtonText,
    deliveryInstructionsCancelButtonText,
    deliveryInstructionsRequiredFieldErrorText,
    deliveryInstructionsProvidedByLasershipText,
    deliveryInstructionsSystemError,
    deliveryInstructionsLeaveAtLabel,
    deliveryInstructionsLeaveAtFrontDoorLabel,
    deliveryInstructionsLeaveAtSideDoorLabel,
    deliveryInstructionsLeaveAtRearDoorLabel,
    deliveryInstructionsLeaveAtGarageLabel,
    deliveryInstructionsLeaveAtReceptionLabel,
    deliveryInstructionsLeaveAtOtherLabel,
    deliveryInstructionsAdditionalNotesLabel,
  } = useTranslations();

  const { bodytextDefaultNone: bodyTextStyle, inputlabelNoneNone } = useTheme();

  const [errorText, setErrorText] = useState('');

  async function onSubmitClick(formData: FormData) {
    setErrorText('');
    try {
      await onSubmit(formData);
    } catch (err) {
      setErrorText(deliveryInstructionsSystemError ?? '');
      throw err;
    }
  }

  return (
    <div style={{ padding: '0.75rem' }}>
      <StepHeader>
        <SemibigHeader as="h2">
          {deliveryInstructionsLeaveInstructionsHeader}
        </SemibigHeader>
      </StepHeader>
      <Paragraph
        containerCustomStyle={{ maxWidth: '33.75rem', margin: '1rem 0 2rem' }}
      >
        {deliveryInstructionsLeaveInstructionsSubheader}
      </Paragraph>
      <ErrorAlert
        show={!!errorText}
        focusOnShow
        error
        dismissible={false}
        role="alert"
        timeout={0}
        s={{
          padding: '1rem',
          margin: '0 0 2rem 0',
          borderRadius: '.25rem',
        }}
      >
        <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
          {errorText}
        </Paragraph>
      </ErrorAlert>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(onSubmitClick)}
          noValidate
        >
          <FormRow>
            <FormColumn sm-columns={12}>
              <SelectList
                featureName={featureName}
                id={InputNames.leaveAt}
                label={deliveryInstructionsLeaveAtLabel ?? ''}
                animateLabel
                errorMessage={errors[InputNames.leaveAt]?.message}
                value={watch(InputNames.leaveAt)}
                onChange={(event) => {
                  // we must explicitly set the value before triggering re-validation
                  setValue(
                    InputNames.leaveAt,
                    event.target.value as unknown as LeaveAtValue,
                  );

                  onLeaveAtChange();

                  // re-validate the note field since it is dependent on this value
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  trigger(InputNames.note);
                }}
                register={register(InputNames.leaveAt, {
                  required: {
                    value: true,
                    message: deliveryInstructionsRequiredFieldErrorText ?? '',
                  },
                })}
              >
                {/*
                aria-label removes the following error message,
                but a screen reader doesn't actually read "State" for the option:
                "A control must be associated with a text label        jsx-a11y/control-has-associated-label"

                This doesn't seem to provide a diminished experience though. If we wanted to improve this though,
                we'd need to bring in something like reachUI/Listbox to have better control.
              */}
                <option
                  value=""
                  aria-label={deliveryInstructionsLeaveAtLabel ?? ''}
                />
                {leaveAtOptions.map((value) => (
                  <option key={value} value={value}>
                    {(() => {
                      switch (value) {
                        case 'Front door':
                          return (
                            deliveryInstructionsLeaveAtFrontDoorLabel ?? ''
                          );
                        case 'Side door':
                          return deliveryInstructionsLeaveAtSideDoorLabel ?? '';
                        case 'Rear door':
                          return deliveryInstructionsLeaveAtRearDoorLabel ?? '';
                        case 'Garage':
                          return deliveryInstructionsLeaveAtGarageLabel ?? '';
                        case 'Reception':
                          return (
                            deliveryInstructionsLeaveAtReceptionLabel ?? ''
                          );
                        case 'other':
                          return deliveryInstructionsLeaveAtOtherLabel ?? '';
                        default:
                          assertNever(value);
                      }
                    })()}
                  </option>
                ))}
              </SelectList>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn sm-columns={12}>
              <div style={{ position: 'relative' }}>
                <TextArea
                  featureName={featureName}
                  id={InputNames.note}
                  label={deliveryInstructionsAdditionalNotesLabel ?? ''}
                  errorMessage={errors[InputNames.note]?.message}
                  value={watch(InputNames.note)}
                  register={register(InputNames.note, {
                    maxLength: 150,
                    validate: () => {
                      const { leaveAt, note } = getValues();

                      if (leaveAt !== 'other') {
                        return true;
                      }

                      if (note.length > 0) {
                        return true;
                      }

                      return deliveryInstructionsRequiredFieldErrorText ?? '';
                    },
                  })}
                  containerOverridesStyle={{ height: '5rem' }}
                />
                <div
                  style={{
                    ...inputlabelNoneNone,
                    textAlign: 'right',
                    position: 'absolute',
                    bottom: '0.5rem',
                    right: '0.5rem',
                    fontSize: '0.75rem',
                  }}
                >
                  {150 - (watch(InputNames.note)?.length ?? 0)}/150
                </div>
              </div>
            </FormColumn>
          </FormRow>
          <DeliveryInstructionsSublimeOption />
          <ActionsRow>
            <Column
              xs-style="display: block; text-align: center;"
              sm-style="display: none"
              xs-columns={12}
            >
              <Button
                big
                type={ButtonType.submit}
                disabled={isSubmitting || !isValid}
              >
                {deliveryInstructionsSubmitInstructionsButtonText}
              </Button>
              <div style={{ marginTop: '1rem' }}>
                <TextLink onClick={onCancel}>
                  {deliveryInstructionsCancelButtonText ?? ''}
                </TextLink>
              </div>
            </Column>
            <FormColumn
              sm-columns={8}
              md-columns={7}
              xs-style="display: none"
              sm-style="display: block"
            >
              <Button type={ButtonType.submit} disabled={isSubmitting}>
                {deliveryInstructionsSubmitInstructionsButtonText ?? ''}
              </Button>
              {/*
              TODO: This should probably use `nth-text-link`, but is not because:
              1) This will need `type` set to button so that it doesn't submit the form. Can't set type on `nth-text-link`
              2) This should probably have some padding on mobile to make it easier to tap. Can't set padding on `nth-text-link`
              3) `nth-text-link` doesn't have theming. It seems the text should be set to the primary button color
              */}
              <span style={{ marginLeft: '0.5rem' }}>
                <TextLink onClick={onCancel}>
                  {deliveryInstructionsCancelButtonText}
                </TextLink>
              </span>
            </FormColumn>
            <LasershipLogoWrapper sm-columns={4} md-columns={5}>
              <div
                // TODO: update this to bodytextEmphasisNone when it gets default style
                style={{
                  ...bodyTextStyle,
                  fontWeight: 'bold',
                }}
              >
                {deliveryInstructionsProvidedByLasershipText}
              </div>
              <LasershipLogo />
            </LasershipLogoWrapper>
          </ActionsRow>
        </form>
      </FormProvider>
    </div>
  );
};

export default withTranslationsProvider(
  withThemeProvider(FormPanel, {
    themeTokens: [
      'bodytextDefaultNone',
      'inputlabelNoneNone',
      'linksTrackingDefault',
    ],
  }),
  {
    translationKeys: [
      'deliveryInstructionsLeaveInstructionsHeader',
      'deliveryInstructionsLeaveInstructionsSubheader',
      'deliveryInstructionsSubmitInstructionsButtonText',
      'deliveryInstructionsCancelButtonText',
      'deliveryInstructionsRequiredFieldErrorText',
      'deliveryInstructionsProvidedByLasershipText',
      'deliveryInstructionsSystemError',
      'deliveryInstructionsLeaveAtLabel',
      'deliveryInstructionsLeaveAtFrontDoorLabel',
      'deliveryInstructionsLeaveAtSideDoorLabel',
      'deliveryInstructionsLeaveAtRearDoorLabel',
      'deliveryInstructionsLeaveAtGarageLabel',
      'deliveryInstructionsLeaveAtReceptionLabel',
      'deliveryInstructionsLeaveAtOtherLabel',
      'deliveryInstructionsAdditionalNotesLabel',
    ],
  },
);
